import { useMemo, useState } from 'react';
import type { components } from '../../../types/schema';

type ChatRoomMessage = components['schemas']['ChatRoomMessage'];
type ChatRoom = components['schemas']['ChatRoom'];

interface UseOptimisticChatMessagesParams {
  baseMessages: ChatRoomMessage[];
  chatRoom?: ChatRoom;
  chatRoomId?: string;
}

interface UseOptimisticChatMessagesResult {
  displayMessages: ChatRoomMessage[];
  onBeforeSend: (content: string) => void;
  onAfterSent: () => void;
  onSendFailed: () => void;
  lastKey: string;
}

export function useOptimisticChatMessages({
  baseMessages,
  chatRoom,
  chatRoomId
}: UseOptimisticChatMessagesParams): UseOptimisticChatMessagesResult {
  const [optimisticMessages, setOptimisticMessages] = useState<ChatRoomMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);

  const displayMessages = useMemo(() => {
    const isTypingMessage = (m: ChatRoomMessage): boolean =>
      m.messageType === 'notice' && m.content === '__typing__';

    const toTimestamp = (m: ChatRoomMessage): number => {
      if (!m?.sentAt) return 0;
      const t = new Date(m.sentAt).getTime();
      return Number.isNaN(t) ? 0 : t;
    };

    const merged = [...baseMessages, ...optimisticMessages].filter((m) => !isTypingMessage(m));

    // 정렬: sentAt ASC → 동시간대엔 내 메시지 우선 → id 사전식
    merged.sort((a, b) => {
      const ta = toTimestamp(a);
      const tb = toTimestamp(b);
      if (ta !== tb) return ta - tb;
      if (a.isMine !== b.isMine) return a.isMine ? -1 : 1;
      return String(a.chatRoomMessageId).localeCompare(String(b.chatRoomMessageId));
    });

    if (isTyping && chatRoom) {
      // 타이핑 인디케이터는 항상 마지막에 추가
      merged.push({
        chatRoomMessageId: `typing-${chatRoomId || 'unknown'}`,
        chatRoomId: chatRoomId || '',
        userId: null,
        userProfileId: null,
        messageType: 'notice' as ChatRoomMessage['messageType'],
        content: '__typing__',
        sentAt: new Date().toISOString(),
        seenAt: null,
        participantType: 'bettie' as ChatRoomMessage['participantType'],
        participantId: null,
        chatRoomUserId: 0,
        displayName: chatRoom.bettieProfile.nickname,
        userProfilePictureUrl: chatRoom.bettieProfile.pictureUrl,
        isMine: false
      } as ChatRoomMessage);
    }

    return merged;
  }, [baseMessages, optimisticMessages, isTyping, chatRoom, chatRoomId]);

  const lastKey = useMemo(
    () => displayMessages[displayMessages.length - 1]?.chatRoomMessageId || '',
    [displayMessages]
  );

  const onBeforeSend = (content: string) => {
    const now = new Date().toISOString();
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimistic: ChatRoomMessage = {
      chatRoomMessageId: tempId,
      chatRoomId: chatRoomId || '',
      userId: null,
      userProfileId: null,
      messageType: 'text' as ChatRoomMessage['messageType'],
      content,
      sentAt: now,
      seenAt: now,
      participantType: 'user' as ChatRoomMessage['participantType'],
      participantId: null,
      chatRoomUserId: 0,
      displayName: '나',
      userProfilePictureUrl: null,
      isMine: true
    } as ChatRoomMessage;
    setOptimisticMessages((prev) => [...prev, optimistic]);
    setIsTyping(true);
  };

  const onAfterSent = () => {
    setOptimisticMessages([]);
    setIsTyping(false);
  };

  const onSendFailed = () => {
    setOptimisticMessages((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev));
    setIsTyping(false);
  };

  return { displayMessages, onBeforeSend, onAfterSent, onSendFailed, lastKey };
}
